import { useEffect, useState } from "react";
import { api, SITE_HOST } from "./api";
import { Landing } from "./pages/Landing";
import { Verify } from "./pages/Verify";
import { Reset } from "./pages/Reset";
import { Dashboard } from "./pages/Dashboard";
import { Logo } from "./components/Logo";
import { ThemeToggle } from "./components/ThemeToggle";

type AuthState =
  | { kind: "loading" }
  | { kind: "anon" }
  | { kind: "customer"; email: string; id: string };

function tokenFromUrl(path: string): string | null {
  if (typeof window === "undefined") return null;
  if (window.location.pathname !== path) return null;
  const token = new URLSearchParams(window.location.search).get("token");
  return token && token.length > 0 ? token : null;
}

// The public marketing site (otp.mioe.com.tr) deep-links into the embedded
// form via ?register / ?login (or the matching hash); pick which tab opens.
// SMS login is the default when there's no hint.
function initialAuthMode(): "sms" | "login" | "signup" {
  if (typeof window === "undefined") return "sms";
  const hint = (window.location.search + window.location.hash).toLowerCase();
  if (/(register|signup|kayit|kayıt)/.test(hint)) return "signup";
  if (/login|giris|giriş|parola|password|sifre|şifre/.test(hint)) return "login";
  return "sms";
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });
  const verifyToken = tokenFromUrl("/verify");
  const resetToken = tokenFromUrl("/reset");

  useEffect(() => {
    if (verifyToken || resetToken) {
      setAuth({ kind: "anon" });
      return;
    }
    api
      .me()
      .then((me) => setAuth({ kind: "customer", email: me.email, id: me.id }))
      .catch(() => setAuth({ kind: "anon" }));
  }, [verifyToken, resetToken]);

  let content;
  if (resetToken) {
    content = <Reset token={resetToken} />;
  } else if (verifyToken) {
    content = (
      <Verify
        token={verifyToken}
        onVerified={(me) => {
          window.history.replaceState(null, "", "/");
          setAuth({ kind: "customer", email: me.email, id: me.id });
        }}
      />
    );
  } else if (auth.kind === "loading") {
    content = (
      <div className="login-shell">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <Logo size={48} title={SITE_HOST} />
          <span className="muted">Loading…</span>
        </div>
      </div>
    );
  } else if (auth.kind === "anon") {
    content = (
      <Landing
        initialMode={initialAuthMode()}
        onLogin={(me) => setAuth({ kind: "customer", email: me.email, id: me.id })}
      />
    );
  } else {
    content = (
      <Dashboard
        meEmail={auth.email}
        onLogout={() => setAuth({ kind: "anon" })}
      />
    );
  }

  return (
    <>
      {content}
      <ThemeToggle />
    </>
  );
}
